import { useCallback, useEffect, useState } from 'react';
import { issueToken } from '@apis/auth';
import type { AuthState, TokenRequest } from '@app-types/auth';
import {
  clearAccessToken,
  getAccessToken,
  persistAccessToken,
  setMemoryAccessToken,
} from '@utils/authStorage';
import {
  AUTH_INVALIDATED_EVENT,
  resetAuthErrorNotification,
} from '@utils/authEvents';
import { getTokenFromSearch } from '@utils/queryParams';

const INITIAL_STATE: AuthState = {
  token: null,
  isAuthenticated: false,
  status: 'initializing',
};

export interface UseAuthResult extends AuthState {
  login: (request: TokenRequest, remember: boolean) => Promise<string>;
  logout: () => void;
}

/** Single auth state owner; AuthGate calls this hook once and passes login down. */
export const useAuth = (): UseAuthResult => {
  const [state, setState] = useState<AuthState>(INITIAL_STATE);

  useEffect(() => {
    const queryToken = getTokenFromSearch(window.location.search);
    if (queryToken) setMemoryAccessToken(queryToken);
    const token = getAccessToken();
    setState({
      token,
      isAuthenticated: Boolean(token),
      status: token ? 'authenticated' : 'anonymous',
    });
  }, []);

  useEffect(() => {
    const handleAuthInvalidated = () => {
      setState({ token: null, isAuthenticated: false, status: 'anonymous' });
    };

    window.addEventListener(AUTH_INVALIDATED_EVENT, handleAuthInvalidated);
    return () => {
      window.removeEventListener(AUTH_INVALIDATED_EVENT, handleAuthInvalidated);
    };
  }, []);

  const login = useCallback(async (request: TokenRequest, remember: boolean) => {
    const response = await issueToken({
      username: request.username.trim(),
      password: request.password,
    });
    resetAuthErrorNotification();
    persistAccessToken(response.token, remember);
    setState({ token: response.token, isAuthenticated: true, status: 'authenticated' });
    return response.token;
  }, []);

  const logout = useCallback(() => {
    clearAccessToken();
    resetAuthErrorNotification();
    setState({ token: null, isAuthenticated: false, status: 'anonymous' });
  }, []);

  return { ...state, login, logout };
};
